/**
 * AuthContext — single source of truth for "who's logged in."
 *
 * Boots by calling /api/auth/me. Result:
 *   - 200 with user JSON  → authenticated
 *   - 401                 → not authenticated, redirect to /login
 *   - network error       → show error UI, let user retry
 *
 * Exposes:
 *   useAuth().user        → current user or null
 *   useAuth().status      → "loading" | "authenticated" | "unauthenticated"
 *   useAuth().logout()    → clear cookies on the server, redirect to Cognito /logout
 *
 * Note: login is initiated from the Login route via startLogin() in lib/auth.ts.
 * We don't expose a login() helper from the context because the OAuth flow
 * leaves the SPA entirely — there's nothing to do besides redirect.
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "";

export interface User {
  sub: string;
  email: string;
  groups: string[];
  is_admin: boolean;
  /**
   * Whether the user can use the AI Analyst feature.
   *
   * Independent of is_admin — admins are NOT automatically granted AI
   * Analyst access. Membership in the `ai_analyst` Cognito group is
   * required, and is granted opt-in by another admin via UserManagement.
   */
  has_ai_analyst: boolean;

  /**
   * Whether the user can use the Assumptions Hub (data entry).
   *
   * Independent of is_admin — admins are NOT automatically granted access.
   * Membership in the `assumptions` Cognito group is required, granted opt-in
   * by another admin via UserManagement.
   */
  has_assumptions: boolean;
  /** Event Reporting read access (event_viewer or event_editor group). */
  has_event_viewer: boolean;
  /** Event Reporting edit access (event_editor group). */
  has_event_editor: boolean;
}

export type AuthStatus = "loading" | "authenticated" | "unauthenticated";

interface AuthContextValue {
  user: User | null;
  status: AuthStatus;
  logout: () => Promise<void>;
  /** Force a re-fetch of /api/auth/me. Used after /callback succeeds. */
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");

  /** Fetch /api/auth/me and update local state. */
  const refresh = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/auth/me`, {
        credentials: "include", // send the HttpOnly cookie
      });

      if (resp.status === 200) {
        const data = await resp.json();
        setUser(data.user);
        setStatus("authenticated");
        return;
      }

      if (resp.status === 401) {
        setUser(null);
        setStatus("unauthenticated");
        return;
      }

      // 5xx or unexpected status — treat as unauthenticated for safety,
      // but surface the issue in the console.
      console.error("auth.me unexpected status", resp.status);
      setUser(null);
      setStatus("unauthenticated");
    } catch (err) {
      // Network error — also treat as unauthenticated so the UI doesn't hang.
      console.error("auth.me network error", err);
      setUser(null);
      setStatus("unauthenticated");
    }
  }, []);

  /** Log out: clear cookies on backend, then redirect to Cognito /logout. */
  const logout = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
      const data = await resp.json();

      // Clear local state.
      setUser(null);
      setStatus("unauthenticated");

      // Redirect to Cognito /logout so its session cookies clear too.
      // (Without this, clicking "Sign In" again would skip the password screen.)
      if (data.cognito_logout_url) {
        window.location.assign(data.cognito_logout_url);
      } else {
        window.location.assign("/login");
      }
    } catch (err) {
      console.error("logout failed", err);
      // Even if the backend call fails, clear local state and send to /login.
      setUser(null);
      setStatus("unauthenticated");
      window.location.assign("/login");
    }
  }, []);

  // Boot: check auth state once on mount.
  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <AuthContext.Provider value={{ user, status, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

/** Hook for consuming auth state from any component. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
